import styles from './plain-login-form.module.css'

import get from 'lodash/get'
import React, { useCallback, useEffect, useState } from 'react'
import type { Location } from 'react-router-dom'

import Recaptcha from '@disco/accounts/components/auth/recaptcha'
import { MFA_SUPPORT_URL } from '@disco/constants/external-links'

import type { AddToDiscoUser, LoginDetails } from 'types/user'

import {
  Box,
  Button as DSButton,
  Flex,
  Link,
  Text,
  TextField,
} from 'components/ds'
import PasswordTextField from 'components/ds/PasswordTextField'
import ButtonContainer from 'components/ui/button-container'

import ErrorMessage from '../error-message'
import Label from '../label'
import LoginButton from '../login-button'
import Separator from '../separator'
import SignUp from '../sign-up'
import { useTranslation } from '@disco/i18n/use-translation'
import { Trans } from '@disco/i18n/trans'

type Props = {
  user: AddToDiscoUser
  location: Location
  onSubmit: (loginDetails: LoginDetails) => unknown
  errorCode?: string | null | undefined
  loginAttempts?: number
  samlEnabled?: boolean
  onSamlClick: () => void
  onForgotPasswordClick: () => void
  onSignUpClick: () => void
  signupUrl?: string | null | undefined
  signupCatalog?: boolean
  isTransitioning?: boolean
}

/**
 * PlainLoginForm component for standard email/password authentication
 *
 * Provides a login form with email and password fields, MFA support, CAPTCHA integration,
 * and options for SSO, forgot password, and signup. Handles form submission and validation.
 *
 * @param props - Component props
 * @param props.user - User state containing login details and authentication status
 * @param props.location - Current location information
 * @param props.onSubmit - Callback function called when form is submitted
 * @param props.errorCode - Error code for display purposes
 * @param props.loginAttempts - Number of login attempts for CAPTCHA threshold
 * @param props.samlEnabled - Whether SAML/SSO login is enabled
 * @param props.onSamlClick - Callback for SAML login button click
 * @param props.onForgotPasswordClick - Callback for forgot password link click
 * @param props.onSignUpClick - Callback for sign up button click
 * @param props.signupUrl - URL for signup if external signup is enabled
 * @param props.signupCatalog - Whether catalog signup is enabled
 * @param props.isTransitioning - Whether the form is in a transitioning state
 * @returns JSX element containing the login form
 */
const PlainLoginForm = ({
  user,
  location,
  samlEnabled,
  onSubmit,
  errorCode,
  loginAttempts = 0,
  onSamlClick,
  onForgotPasswordClick,
  onSignUpClick,
  signupUrl,
  signupCatalog,
  isTransitioning,
}: Props): React.ReactNode => {
  const { t } = useTranslation('accounts')
  const [lastCredentials, setLastCredentials] = useState({
    username: '',
    password: '',
  })
  const [captchaToken, setCaptchaToken] = useState('')
  const [requireCaptcha, setRequireCaptcha] = useState(false)
  const { showLoginError, mfaCodeRequired, loggingIn, mfaType } = user
  // only change view if we are NOT logging in
  const [showMfa, setShowMfa] = useState(mfaCodeRequired)
  useEffect(() => {
    if (loggingIn) return
    setShowMfa(mfaCodeRequired)
  }, [mfaCodeRequired, loggingIn])

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    // Get the form data
    const formData = new FormData(e.target as HTMLFormElement)
    const username = formData.get('username') || lastCredentials.username || ''
    const password = formData.get('password') || lastCredentials.password || ''
    const mfaCode = formData.get('mfaCode') || ''
    // Ensure the values are strings
    if (
      typeof username !== 'string' ||
      typeof password !== 'string' ||
      typeof mfaCode !== 'string'
    )
      throw new Error('Invalid username or password field')
    const usernameClean = username.trim()
    const mfaCodeClean = mfaCode.trim().replace(' ', '')
    // Save the last credentials
    setLastCredentials({
      username: usernameClean,
      password,
    })
    onSubmit({
      username: usernameClean,
      password,
      captcha: captchaToken,
      mfa_code: mfaCodeClean,
    })
  }

  const handleCaptchaToken = useCallback((token: string) => {
    setCaptchaToken(token)
    setRequireCaptcha(true)
  }, [])
  const signupEnabled = signupUrl || signupCatalog
  return (
    <Flex asChild direction="column" gap="3">
      <form onSubmit={handleSubmit} autoComplete="on">
        <ErrorMessage user={user} location={location} />

        {!showMfa ? (
          <>
            <Flex direction="column" gap="2">
              <Label
                htmlFor="email"
                caption={t('accounts:yourEmail', 'Your email')}
              />
              <TextField.Root
                size="3"
                id="email"
                name="username"
                type="text"
                defaultValue={get(user, 'addToDiscoLoginDetails.username', '')}
                placeholder={t(
                  'accounts:pleaseEnterYourEmail',
                  'Please enter your email',
                )}
                autoComplete="username"
                autoCapitalize="off"
                disabled={loggingIn}
              />
            </Flex>
            <Flex direction="column" gap="2">
              <Label
                htmlFor="password"
                caption={t('accounts:password', 'Password')}
              />
              <PasswordTextField
                size="3"
                id="password"
                name="password"
                type="password"
                defaultValue={get(user, 'addToDiscoLoginDetails.password', '')}
                placeholder={t(
                  'accounts:pleaseEnterYourPassword',
                  'Please enter your password',
                )}
                autoComplete="current-password"
                disabled={loggingIn}
              />
            </Flex>
          </>
        ) : (
          <Box>
            <Label
              htmlFor="mfaCode"
              caption={
                mfaType === 'email'
                  ? t(
                      'accounts:enterTheVerificationCodeSentToYourEmail',
                      'Enter the verification code sent to your email',
                    )
                  : t('accounts:verificationCode', 'Verification code')
              }
            />
            <TextField.Root
              size="3"
              id="mfaCode"
              name="mfaCode"
              type="text"
              defaultValue=""
              placeholder={t('accounts:enterCode', 'Enter code')}
              autoComplete="off" // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
          </Box>
        )}

        <ButtonContainer vertical>
          <LoginButton
            user={user}
            captchaToken={captchaToken}
            errorCode={errorCode}
            isTransitioning={isTransitioning}
          />
          <Flex direction="column" gap="2" mt="2">
            {showMfa ? (
              <Text align="center" size="2">
                <Trans
                  t={t}
                  i18nKey="accounts:havingTroubleLoggingInLearnMoreAboutMfa"
                >
                  Having Trouble Logging in?
                  <br />
                  <Link
                    href={MFA_SUPPORT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="always"
                  >
                    Learn more about MFA
                  </Link>
                </Trans>
              </Text>
            ) : (
              <>
                <Text align="center" size="2">
                  <Link
                    href="#"
                    onClick={e => {
                      e.preventDefault()
                      onForgotPasswordClick()
                    }}
                    color="gray"
                  >
                    {t('accounts:forgotPassword', 'Forgot password?')}
                  </Link>
                </Text>
                {(samlEnabled || signupEnabled) && <Separator or />}
                {samlEnabled && (
                  <DSButton
                    type="button"
                    size="3"
                    variant="soft"
                    color="gray"
                    className={styles.FullWidthButton}
                    onClick={onSamlClick}
                  >
                    {t('accounts:signInWithSso', 'Sign in with SSO')}
                  </DSButton>
                )}
                {signupEnabled && (
                  <SignUp signupUrl={signupUrl} onClick={onSignUpClick} />
                )}
              </>
            )}
          </Flex>
        </ButtonContainer>

        {showLoginError &&
          (errorCode === 'CAPTCHA_REQUIRED' || requireCaptcha) && (
            <Recaptcha
              type="login"
              tokenRequests={loginAttempts}
              onTokenReceived={handleCaptchaToken}
            />
          )}
      </form>
    </Flex>
  )
}

export default PlainLoginForm
